import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

const EVENT_TYPES = [
  { value: '', label: 'All events' },
  { value: 'login_success', label: 'Login success' },
  { value: 'login_failure', label: 'Login failure' },
  { value: 'logout', label: 'Logout' },
  { value: 'password_change', label: 'Password change' },
  { value: 'impersonation_start', label: 'Impersonation start' },
  { value: 'impersonation_stop', label: 'Impersonation stop' },
  { value: 'clock_in', label: 'Clock in' },
  { value: 'clock_out', label: 'Clock out' },
  { value: 'time_entry_created', label: 'Time entry (web)' },
  { value: 'entity_write', label: 'Business record (quote, order, part, …)' },
  { value: 'api_write', label: 'API action (other)' }
];

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return String(iso);
  }
}

function ActivityRow({ row, employeeLabel }) {
  const [open, setOpen] = useState(false);
  const hasDetail = !!(row.details || row.http_path || row.user_agent || row.actor_email);

  return (
    <>
      <TableRow hover>
        <TableCell>{formatTs(row.created_at)}</TableCell>
        <TableCell>{employeeLabel(row)}</TableCell>
        <TableCell>
          <Chip label={row.event_type || '—'} size="small" variant="outlined" />
        </TableCell>
        <TableCell sx={{ maxWidth: 130, wordBreak: 'break-word' }}>
          {row.entity_type
            ? `${row.entity_type}${row.entity_id != null ? ` #${row.entity_id}` : ''}`
            : '—'}
        </TableCell>
        <TableCell sx={{ maxWidth: 360, wordBreak: 'break-word' }}>{row.summary || '—'}</TableCell>
        <TableCell>{row.ip_address || '—'}</TableCell>
        <TableCell padding="checkbox" align="right">
          {hasDetail ? (
            <IconButton size="small" onClick={() => setOpen(!open)} aria-label="show details">
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, borderTop: 0, bgcolor: 'action.hover' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.5, px: 1 }}>
                {row.actor_email && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Actor email:</strong> {row.actor_email}
                  </Typography>
                )}
                {(row.http_method || row.http_path) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>HTTP:</strong> {row.http_method} {row.http_path}
                    {row.http_status != null ? ` → ${row.http_status}` : ''}
                  </Typography>
                )}
                {row.user_agent && (
                  <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-all' }}>
                    <strong>User-Agent:</strong> {row.user_agent}
                  </Typography>
                )}
                {row.details && (
                  <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                    {typeof row.details === 'object' ? JSON.stringify(row.details, null, 2) : String(row.details)}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const ActivityAuditLog = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [eventType, setEventType] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axios.get('/api/employees', { params: { limit: 500 } });
      const list = res.data?.data ?? res.data ?? [];
      setEmployees(Array.isArray(list) ? list : []);
    } catch {
      setEmployees([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(eventType ? { event_type: eventType } : {}),
        ...(employeeId ? { employee_id: employeeId } : {})
      };
      const res = await axios.get('/api/admin/activity-log', { params });
      const raw = res.data?.data ?? [];
      const normalized = raw.map((r) => ({
        ...r,
        details: typeof r.details === 'string' ? tryParseJson(r.details) : r.details
      }));
      setRows(normalized);
      setTotal(res.data?.pagination?.total ?? 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load activity log');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, dateFrom, dateTo, eventType, employeeId]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    load();
  }, [load]);

  const employeeLabel = (row) => {
    if (row.employee_first_name || row.employee_last_name) {
      return [row.employee_first_name, row.employee_last_name].filter(Boolean).join(' ');
    }
    if (row.employee_email) return row.employee_email;
    if (row.actor_email) return `(attempt) ${row.actor_email}`;
    return '—';
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign-ins, sign-outs, kiosk and web punches, password changes, impersonation, targeted business changes (quotes,
        orders, parts, invoices, POs, vendors, customers, and related records), and other authenticated API writes.
        Failed logins are recorded by email without an employee id. Logout is recorded when the app calls the sign-out API.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          type="date"
          label="From"
          value={dateFrom}
          onChange={(e) => {
            setPage(0);
            setDateFrom(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={dateTo}
          onChange={(e) => {
            setPage(0);
            setDateTo(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          select
          label="Event"
          value={eventType}
          onChange={(e) => {
            setPage(0);
            setEventType(e.target.value);
          }}
          sx={{ minWidth: 200 }}
        >
          {EVENT_TYPES.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Employee"
          value={employeeId}
          onChange={(e) => {
            setPage(0);
            setEmployeeId(e.target.value);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={String(e.id)}>
              {[e.first_name, e.last_name].filter(Boolean).join(' ')} ({e.email})
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" size="small" onClick={() => load()}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={160}>When (UTC)</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Record</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>IP</TableCell>
              <TableCell width={56} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No activity in this range.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => <ActivityRow key={row.id} row={row} employeeLabel={employeeLabel} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
};

function tryParseJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export default ActivityAuditLog;
